import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Images, Play, Heart, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import ImageUploader from "@/components/ImageUploader";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MediaAlbum({ userId }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [pendingUrl, setPendingUrl] = useState("");
  const [pendingType, setPendingType] = useState("photo");
  const [caption, setCaption] = useState("");

  const isOwn = user?.user_id === userId;

  const load = async () => {
    try {
      const res = await axios.get(`${API}/media/user/${userId}`);
      setItems(res.data);
    } catch (err) {
      console.error("[MediaAlbum] load failed", err);
    }
  };

  useEffect(() => {
    if (userId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleUpload = async (url, mediaType) => {
    setPendingUrl(url);
    setPendingType(mediaType);
  };

  const publish = async () => {
    if (!pendingUrl) return;
    try {
      await axios.post(`${API}/media`, { url: pendingUrl, media_type: pendingType, caption });
      toast.success("Adicionado ao álbum!");
      setPendingUrl("");
      setCaption("");
      load();
    } catch { toast.error("Erro"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover do álbum?")) return;
    await axios.delete(`${API}/media/${id}`);
    load();
  };

  const toggleLike = async (id) => {
    const res = await axios.post(`${API}/media/${id}/like`);
    setItems(items.map(m => m.media_id === id ? { ...m, likes: res.data.likes } : m));
  };

  return (
    <Card className="p-6" data-testid="media-album">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Images className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-bold tracking-tight">Álbum</h2>
            <p className="text-xs text-muted-foreground">Fotos e vídeos {isOwn ? "seus" : "do usuário"} ({items.length})</p>
          </div>
        </div>
        {isOwn && (
          <ImageUploader
            testid="album-upload-btn"
            variant="default"
            size="sm"
            label="Enviar mídia"
            accept="image/*,video/*"
            className="rounded-full"
            onUploaded={handleUpload}
          />
        )}
      </div>

      {/* Pending publish */}
      {isOwn && pendingUrl && (
        <div className="mb-5 p-3 rounded-xl border-2 border-dashed border-primary/40 bg-primary/5">
          <div className="flex gap-3 items-start">
            {pendingType === "photo" ? (
              <img src={pendingUrl} alt="preview" className="w-24 h-24 rounded-lg object-cover" />
            ) : (
              <video src={pendingUrl} className="w-24 h-24 rounded-lg object-cover" muted />
            )}
            <div className="flex-1 space-y-2">
              <Input
                data-testid="album-caption"
                placeholder="Legenda (opcional)"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
              />
              <div className="flex gap-2">
                <Button data-testid="album-publish" onClick={publish} size="sm" className="rounded-full">Publicar</Button>
                <Button variant="ghost" size="sm" onClick={() => { setPendingUrl(""); setCaption(""); }}>Cancelar</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="p-10 text-center text-muted-foreground">
          <Images className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Nenhuma mídia ainda</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {items.map((m) => (
            <button
              key={m.media_id}
              data-testid={`media-${m.media_id}`}
              onClick={() => setSelected(m)}
              className="relative aspect-square rounded-xl overflow-hidden bg-muted group hover-lift"
            >
              {m.media_type === "photo" ? (
                <img src={m.url} alt={m.caption} className="w-full h-full object-cover" />
              ) : (
                <>
                  <video src={m.url} className="w-full h-full object-cover" muted />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center">
                      <Play className="w-5 h-5 text-primary fill-primary ml-0.5" />
                    </div>
                  </div>
                </>
              )}
              {(m.likes?.length || 0) > 0 && (
                <div className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                  <Heart className="w-3 h-3 fill-current" /> {m.likes.length}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden">
          {selected && (
            <div className="flex flex-col">
              <div className="bg-black flex items-center justify-center max-h-[70vh]">
                {selected.media_type === "photo" ? (
                  <img src={selected.url} alt={selected.caption} className="max-h-[70vh] w-full object-contain" />
                ) : (
                  <video src={selected.url} controls autoPlay className="max-h-[70vh] w-full" />
                )}
              </div>
              <div className="p-4">
                {selected.caption && <p className="text-sm mb-3">{selected.caption}</p>}
                <div className="flex items-center justify-between">
                  <button
                    data-testid={`like-media-${selected.media_id}`}
                    onClick={() => toggleLike(selected.media_id)}
                    className={`flex items-center gap-2 text-sm ${selected.likes?.includes(user?.user_id) ? "text-red-500" : ""}`}
                  >
                    <Heart className={`w-5 h-5 ${selected.likes?.includes(user?.user_id) ? "fill-current" : ""}`} />
                    {selected.likes?.length || 0}
                  </button>
                  {isOwn && (
                    <Button variant="ghost" size="sm" onClick={() => { remove(selected.media_id); setSelected(null); }}>
                      <Trash2 className="w-4 h-4 mr-2 text-destructive" /> Remover
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
